/**
 * ChatScreen — shell: session sidebar + header + the per-session conversation.
 * Conversation state lives in <ChatConversation key={sessionId}>; switching
 * sessions remounts it (clean streaming reset + SSE abort).
 */
import React, { useMemo, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  Modal,
  useWindowDimensions,
  ActivityIndicator,
  StyleSheet,
} from 'react-native';
import { useRoute, RouteProp } from '@react-navigation/native';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ChatNavigatorParamList } from '../../navigation/ChatNavigator';
import { ChatConversation } from './ChatConversation';
import { SessionSidebar } from '../../components/chat/SessionSidebar';
import { ConsentScreen } from '../../components/chat/ConsentScreen';
import { useChatSessions } from '../../hooks/useChatSessions';
import { useMainSession } from '../../hooks/useMainSession';
import { getConsent } from '../../services/chatApi';
import { useTheme } from '../../contexts/ThemeContext';

const WIDE_BREAKPOINT = 768;

type Params = RouteProp<ChatNavigatorParamList, 'ChatScreen'>;

export function ChatScreen() {
  const route = useRoute<Params>();
  const {
    sessionId: routeSessionId,
    initialMessage,
    autoSend,
    initialCommand,
    discussSeed,
  } = route.params ?? {};
  const { theme } = useTheme();
  const { t } = useTranslation();
  const styles = useMemo(() => createThemedStyles(theme), [theme]);
  const { width } = useWindowDimensions();
  const wide = width >= WIDE_BREAKPOINT;
  const [collapsed, setCollapsed] = useState(false); // wide layout
  const [drawerOpen, setDrawerOpen] = useState(false); // narrow layout

  const consent = useQuery({
    queryKey: ['chat-consent'],
    queryFn: getConsent,
    staleTime: Infinity,
  });
  const mainSession = useMainSession();
  // Main-preference applies ONLY to the plain tab-open-without-params case:
  // route deep-links (sessionId / initialMessage / autoSend / initialCommand /
  // discussSeed) keep targeting the session they named, exactly like the
  // appliesToRoute gating below. While the main resolve is pending, `undefined`
  // lets useChatSessions pick the latest and migrate onto main when it lands;
  // a failed resolve passes `null` = fall back to the latest for real.
  const plainOpen =
    routeSessionId == null &&
    initialMessage == null &&
    autoSend == null &&
    initialCommand == null &&
    discussSeed == null;
  const mainPreference = plainOpen
    ? mainSession.isError
      ? null
      : mainSession.data?.id
    : undefined;
  const sessions = useChatSessions(routeSessionId ?? null, mainPreference);

  if (consent.isLoading || sessions.isLoading) {
    return (
      <View style={[styles.container, styles.center]}>
        <ActivityIndicator size="large" color={theme.colors.primary} />
      </View>
    );
  }
  if (consent.data && consent.data.accepted === false) {
    return <ConsentScreen onAccepted={() => consent.refetch()} />;
  }

  const sidebar = (
    <SessionSidebar
      activeSessions={sessions.activeSessions}
      archivedSessions={sessions.archivedSessions}
      currentSessionId={sessions.currentSessionId}
      onSelect={(id) => {
        sessions.setCurrentSessionId(id);
        setDrawerOpen(false);
      }}
      onNewChat={() => {
        void sessions.createSession();
        setDrawerOpen(false);
      }}
      onArchive={(id) => void sessions.archiveSession(id)}
      onUnarchive={(id) => void sessions.unarchiveSession(id)}
      onRename={(id, title) => void sessions.renameSession(id, title)}
      onDelete={(id) => void sessions.deleteSession(id)}
    />
  );

  // Route deep-links (initialMessage/autoSend) only apply to the session they
  // targeted (or to whichever session resolves when the link had no id).
  const appliesToRoute = routeSessionId == null || routeSessionId === sessions.currentSessionId;

  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.headerRow}>
        <Pressable
          onPress={() => (wide ? setCollapsed(!collapsed) : setDrawerOpen(true))}
          accessibilityRole="button"
          accessibilityLabel={t('chat.sessions.panel')}
          style={styles.menuToggle}
        >
          <Text style={styles.menuToggleText}>☰</Text>
        </Pressable>
        <Text style={styles.headerTitle} numberOfLines={1}>
          {sessions.currentSession?.title ?? t('chat.newSessionTitle')}
        </Text>
        {sessions.currentSession?.status === 'archived' && (
          <Pressable
            onPress={() =>
              sessions.currentSessionId && void sessions.unarchiveSession(sessions.currentSessionId)
            }
            style={styles.archivedChip}
          >
            <Text style={styles.archivedChipText}>
              {t('chat.sessions.archived')} · {t('chat.sessions.unarchive')}
            </Text>
          </Pressable>
        )}
      </View>
      <View style={styles.bodyRow}>
        {wide && !collapsed && <View style={styles.sidebarPane}>{sidebar}</View>}
        {!wide && (
          <Modal
            visible={drawerOpen}
            transparent
            animationType="slide"
            onRequestClose={() => setDrawerOpen(false)}
          >
            <Pressable style={styles.drawerScrim} onPress={() => setDrawerOpen(false)}>
              <View style={styles.drawerPane} onStartShouldSetResponder={() => true}>
                {sidebar}
              </View>
            </Pressable>
          </Modal>
        )}
        {sessions.currentSessionId ? (
          <ChatConversation
            key={sessions.currentSessionId}
            sessionId={sessions.currentSessionId}
            initialMessage={appliesToRoute ? initialMessage : undefined}
            autoSend={appliesToRoute ? autoSend : undefined}
            initialCommand={appliesToRoute ? initialCommand : undefined}
            discussSeed={appliesToRoute ? discussSeed : undefined}
          />
        ) : (
          <View style={styles.emptyState}>
            <Text style={styles.emptyTitle}>{t('chat.sessions.noSessions')}</Text>
            <Pressable onPress={() => void sessions.createSession()} style={styles.emptyCta}>
              <Text style={styles.emptyCtaText}>{t('chat.sessions.startNewChat')}</Text>
            </Pressable>
          </View>
        )}
      </View>
    </View>
  );
}

function createThemedStyles(theme: any) {
  return StyleSheet.create({
    container: { flex: 1, overflow: 'hidden' },
    center: { alignItems: 'center', justifyContent: 'center' },
    headerRow: {
      flexDirection: 'row',
      alignItems: 'center',
      padding: theme.spacing.sm,
      borderBottomWidth: 1,
      borderBottomColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    menuToggle: { padding: theme.spacing.sm },
    menuToggleText: { fontSize: theme.typography.fontSizeLg, color: theme.colors.text },
    headerTitle: {
      flex: 1,
      fontSize: theme.typography.fontSizeLg,
      fontWeight: theme.typography.fontWeightBold,
      color: theme.colors.text,
    },
    archivedChip: {
      paddingVertical: theme.spacing.xs,
      paddingHorizontal: theme.spacing.sm,
      borderRadius: theme.radii.md,
      backgroundColor: theme.colors.surfaceSecondary,
    },
    archivedChipText: { fontSize: theme.typography.fontSizeSm, color: theme.colors.textSecondary },
    bodyRow: { flex: 1, flexDirection: 'row' },
    sidebarPane: {
      width: 280,
      borderRightWidth: 1,
      borderRightColor: theme.colors.border,
      backgroundColor: theme.colors.surface,
    },
    drawerScrim: { flex: 1, backgroundColor: 'rgba(0,0,0,0.4)' },
    drawerPane: {
      width: '80%',
      maxWidth: 320,
      height: '100%',
      backgroundColor: theme.colors.surface,
    },
    emptyState: {
      flex: 1,
      alignItems: 'center',
      justifyContent: 'center',
      padding: theme.spacing.md,
    },
    emptyTitle: { fontSize: theme.typography.fontSizeMd, color: theme.colors.textSecondary },
    emptyCta: {
      marginTop: theme.spacing.md,
      paddingVertical: theme.spacing.sm,
      paddingHorizontal: theme.spacing.md,
      borderRadius: theme.radii.md,
      backgroundColor: theme.colors.primary,
    },
    emptyCtaText: { color: theme.colors.textInverse, fontWeight: '600' },
  });
}
